/**
 * Account mapping utilities for StructureNote
 * Maps currency to corresponding account fields
 * Replaces switch statement from original lines 276-297
 */

import { InvestorAccountData, CurrencyAccountFields } from "../StructureNote.types";

/**
 * Get currency-specific account data from investor account
 * Original logic from lines 276-297
 *
 * @param accountData - Full investor account data
 * @param currency - Currency type (SGD or USD)
 * @returns Mapped account fields for the specified currency
 */
export const getCurrencyAccountData = (
  accountData: InvestorAccountData,
  currency: string
): CurrencyAccountFields => {
  // Mapping configuration matching original switch logic
  const fieldMapping: Record<string, CurrencyAccountFields> = {
    SGD: {
      bankName: accountData.investorSgdBankName,
      accountHolder: accountData.investorSgdBankAccountHolder,
      accountNumber: accountData.investorSgdBankAccountId,
      bicSwift: accountData.investorSgdBicSwift,
      branchCode: accountData.investorSgdBranchCode,
      bankCode: accountData.investorSgdBankCode,
      bankAddress: accountData.investorSgdBankAddress,
    },
    USD: {
      bankName: accountData.investorUsdBankName,
      accountHolder: accountData.investorUsdBankAccountHolder,
      accountNumber: accountData.investorUsdBankAccountId,
      bicSwift: accountData.investorUsdBicSwift,
      branchCode: accountData.investorUsdBranchCode,
      bankCode: accountData.investorUsdBankCode,
      bankAddress: accountData.investorUsdBankAddress,
    },
  };

  return fieldMapping[currency] || fieldMapping.SGD;
};

/**
 * Apply currency account data to account object
 * Mutates accountData object with currency-specific fields (maintains original behavior)
 *
 * @param accountData - Account data object to mutate
 * @param currency - Currency type
 */
export const applyCurrencyAccountData = (
  accountData: InvestorAccountData,
  currency: string
): void => {
  const currencyFields = getCurrencyAccountData(accountData, currency);

  // Mutate original object (maintaining original behavior from lines 278-297)
  accountData.bankName = currencyFields.bankName;
  accountData.accountHolder = currencyFields.accountHolder;
  accountData.accountNumber = currencyFields.accountNumber;
  accountData.bicSwift = currencyFields.bicSwift;
  accountData.branchCode = currencyFields.branchCode;
  accountData.bankCode = currencyFields.bankCode;
  accountData.bankAddress = currencyFields.bankAddress;
};
